import { cn } from "@/lib/utils";

/**
 * mola's mark: a molar on a 12×12 pixel grid, one string per row, "#" filled.
 * The gap in rows 2–3 is the highlight. public/favicon.svg draws the same grid;
 * change both together.
 */
const TOOTH = [
  "..##....##..",
  ".####..####.",
  "##.#########",
  "##.#########",
  "############",
  "############",
  ".##########.",
  ".##########.",
  ".####..####.",
  "..###..###..",
  "..##....##..",
  "...#....#...",
] as const;

/** Three rows per colour band, crown to roots; the colours are theme tokens. */
const BAND_FILLS = ["fill-tooth-1", "fill-tooth-2", "fill-tooth-3", "fill-tooth-4"] as const;
const ROWS_PER_BAND = TOOTH.length / BAND_FILLS.length;

/** One SVG subpath per horizontal run of filled cells; `top` is the first row's y. */
export function pixelPath(rows: readonly string[], top = 0): string {
  return rows
    .flatMap((row, i) =>
      [...row.matchAll(/#+/g)].map(
        (run) => `M${run.index} ${top + i}h${run[0].length}v1h-${run[0].length}z`,
      ),
    )
    .join("");
}

const BANDS = BAND_FILLS.map((fill, band) => {
  const top = band * ROWS_PER_BAND;
  return { fill, d: pixelPath(TOOTH.slice(top, top + ROWS_PER_BAND), top) };
});

/**
 * Decorative: the text beside it ("mola") carries the name. Render it at
 * multiples of 12px (size-3, size-6) so every cell lands on whole pixels.
 */
export function ToothIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 12 12"
      shapeRendering="crispEdges"
      className={cn("shrink-0", className)}
    >
      {BANDS.map(({ fill, d }) => (
        <path key={fill} className={fill} d={d} />
      ))}
    </svg>
  );
}
